import type { TFunction } from "i18next";
import {
  deltaColumnId,
  deltaImpactColumnId,
  deltaHeader,
  deltaImpactHeader,
  type CompareTranslationKey
} from "@/entities/dataTables/compareTo/columnIds";
import {
  buildCompareExport,
  type CompareExport
} from "@/entities/dataTables/compareTo/compareExport";
import {
  COMPANY_COMPARE_METRIC_KEYS,
  DEFAULT_COMPARE_PERIOD,
  type CompanyCompareMetricKey,
  type CompareToSelection
} from "@/entities/dataTables/compareTo/types";

// The Companies table's Compare-to copy. Its strings name companies, so it
// cannot share the Contracts namespace.
export const COMPANY_COMPARE_TRANSLATION_KEY: CompareTranslationKey =
  "companies.compareTo";

// Every Δ and Impact column id the feature can produce, in table order.
// Enumerated rather than matched on the `Delta` suffix — a visible custom field
// named "Price Delta" becomes `priceDelta`, and `useEntityExport` drops exact
// id matches, so a suffix test would take a legitimate column out of the CSV.
export const COMPANY_COMPARE_COLUMN_IDS: readonly string[] =
  COMPANY_COMPARE_METRIC_KEYS.flatMap((metricKey) => [
    deltaColumnId(metricKey),
    deltaImpactColumnId(metricKey)
  ]);

const isCompareMetric = (columnKey: string): boolean =>
  (COMPANY_COMPARE_METRIC_KEYS as readonly string[]).includes(columnKey);

/**
 * The table's visible column keys with each Δ pair inserted after the metric it
 * describes.
 *
 * A Δ column carries the same figures as its base metric, so it rides that
 * metric's visibility rather than being gated separately: a tenant that hid ARR
 * must not be offered Δ ARR. Ordering matters too — the pair is listed beside
 * its metric in Columns management, not appended at the end.
 */
export const withCompareToColumnKeys = (
  visibleColumnKeys: readonly string[],
  compareToEnabled: boolean
): string[] =>
  visibleColumnKeys.flatMap((columnKey) =>
    compareToEnabled && isCompareMetric(columnKey)
      ? [columnKey, deltaColumnId(columnKey), deltaImpactColumnId(columnKey)]
      : [columnKey]
  );

/**
 * Picker labels for the Δ columns.
 *
 * Their grid headers are composed at render time from the metric label and the
 * selected timeframe, so Columns management has to be told the same text or the
 * checkbox and the header disagree about what the column is.
 */
export const getCompareToLabelOverrides = (
  compareToEnabled: boolean,
  timeframe: string,
  t: TFunction
): Map<string, string> => {
  const overrides = new Map<string, string>();
  if (!compareToEnabled) return overrides;
  COMPANY_COMPARE_METRIC_KEYS.forEach((metricKey: CompanyCompareMetricKey) => {
    const metricLabel = t(`companies.${metricKey}`);
    overrides.set(
      deltaColumnId(metricKey),
      deltaHeader(metricLabel, timeframe)
    );
    overrides.set(
      deltaImpactColumnId(metricKey),
      deltaImpactHeader(
        metricLabel,
        timeframe,
        t,
        COMPANY_COMPARE_TRANSLATION_KEY
      )
    );
  });
  return overrides;
};

/**
 * What the Companies export sends for its Compare-to columns.
 *
 * companiesapi computes the Δ and Impact cells server-side for the period in
 * the export's filter, so every id exports while the feature is on. Companies
 * have a single currency basis, so there is no inactive basis to withhold:
 * the exportable set and the full set are the same list. With the feature off
 * every id stays excluded, because a saved view built while it was on keeps
 * its Δ ids after it closes.
 *
 * The ids need no `exportColumnIdMap`: `arrDeltaImpact` decamelizes to the
 * backend's `arr_delta_impact`, and so on for each metric. Δ columns need no
 * `columnCurrencyFields` either — the backend declares them as currency in the
 * account's system currency itself.
 */
export const getCompanyCompareExport = (
  compareToEnabled: boolean,
  selection: CompareToSelection | undefined,
  t: TFunction
): CompareExport =>
  buildCompareExport({
    enabled: compareToEnabled,
    selection,
    allColumnIds: COMPANY_COMPARE_COLUMN_IDS,
    exportableColumnIds: COMPANY_COMPARE_COLUMN_IDS,
    translationKey: COMPANY_COMPARE_TRANSLATION_KEY,
    t
  });

/**
 * What the table starts on and Reset returns to. Here rather than in
 * `Companies.tsx` so it can be asserted without rendering the page — the
 * override it replaced had no test at all.
 */
export const COMPANY_DEFAULT_COMPARE_TO: CompareToSelection = {
  period: DEFAULT_COMPARE_PERIOD
};
